"use client";
import React, { useState } from "react";
import styles from "./SpecificationSection.module.scss";
import ImageAtom from "@components/atoms/Image";
import assetLoader from "@utils/internalImageLoder";
import MileageModal from "@components/molecules/MileageModal";
import { getSelectedCityClient } from "@helpers/client/getSelectedCity";
import { PAGE_TYPES } from "@constants/pageTypes";
import { formatDeliveryDate } from "@utils/formatDate";
import { formatToLakhs } from "@utils/numberFormatter";

interface DropdownVariant {
  id: string;
  name: string;
  slug: string;
}

interface DetailedVariant {
  variantName: string;
  fuelType: string;
  engine: string;
  emission: string;
  transmission: string;
  slug: string;
  marketPrice: number;
  adspPrice: number;
  ackoDriveSavings: number;
  exShowRoomPrice: number;
  onRoadPrice: number;
  mileage: {
    value: number;
    unit: string;
  };
  variantLevel: string;
  expressDelivery: boolean;
  expressDeliveryDays: number;
  standardDeliveryDays: number;
  status: string;
}

interface VariantSelectorProps {
  variants: DropdownVariant[];
  selectedVariantId: string;
  detailedVariants: DetailedVariant[];
  brandName?: string;
  modelName?: string;
  variantName?: string;
  isSellable?: boolean;
  pageType?: string;
  carAvailability?: string;
  isLoggedIn?: boolean;
  cityName?: string;
  onVariantSelect: (variant: { id?: string; slug?: string }) => Promise<void>;
  isChangingVariant?: boolean;
}

export const VariantSelector: React.FC<VariantSelectorProps> = ({
  variants,
  selectedVariantId,
  detailedVariants,
  brandName,
  modelName,
  variantName,
  isSellable,
  pageType,
  carAvailability,
  isLoggedIn,
  cityName,
  onVariantSelect,
  isChangingVariant = false,
}) => {
  const { name: clientCityName } = getSelectedCityClient();
  const currentCityName = cityName || clientCityName;
  const [showModal, setShowModal] = useState(false);

  const currentVariant = variants?.find((v) => v?.id === selectedVariantId);

  const transformedVariants = detailedVariants.map((variant) => ({
    id: variant?.slug,
    name: variant?.variantName,
    fuelType: variant?.fuelType,
    transmission: variant?.transmission,
    mileage: `${variant?.mileage?.value}`,
    price: variant?.onRoadPrice?.toString(),
    formattedPrice: formatToLakhs(variant?.onRoadPrice),
    expressDeliveryDate: formatDeliveryDate(variant?.expressDeliveryDays),
    expressDelivery: variant?.expressDelivery,
    standardDelivery: formatDeliveryDate(variant?.standardDeliveryDays),
    slug: variant?.slug,
    unit: variant?.mileage?.unit,
    status: variant?.status,
  }));

  const handleVariantChange = async (variant: { id?: string; slug?: string }) => {
    setShowModal(false);
    await onVariantSelect(variant);
  };

  return (
    <>
      <div className={styles.specs__variant}>
        <button
          className={styles.specs__variantButton}
          onClick={() => setShowModal(!showModal)}
          aria-busy={isChangingVariant}
        >
          <span>{currentVariant?.name}</span>
          {!showModal ? (
            <ImageAtom
              src={`${assetLoader()}/icons/Chevron_down_dark.svg`}
              width={20}
              height={20}
              alt="chevron-down"
              loading="lazy"
              className={styles.specs__variantIcon}
            />
          ) : (
            <ImageAtom
              src={`${assetLoader()}/icons/Chevron_up_dark.svg`}
              width={20}
              height={20}
              alt="chevron-up"
              loading="lazy"
              className={styles.specs__variantIcon}
            />
          )}
        </button>
        {isChangingVariant && (
          <span className={styles.specs__variantStatus} role="status">
            Loading specifications…
          </span>
        )}
      </div>

      <MileageModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Select a variant"
        variants={transformedVariants}
        onSelect={handleVariantChange}
        preventScoll={false}
        isInternalUse={true}
        isSellable={isSellable || false}
        segmentPageType={pageType || PAGE_TYPES.VARIANT_PAGE}
        segmentActionName="tap_btn_choose_a_variant"
        segmentSection="Specifications section"
        cityName={currentCityName}
        brandName={brandName}
        modelName={modelName}
        isLoggedIn={isLoggedIn}
        carAvailability={carAvailability}
      />
    </>
  );
};
